import React, { useState } from 'react';
import { PricingTiers } from '../../../components/marketing/PricingTiers';
import { PageLayout } from '../../../components/layout/PageLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Loader2, CreditCard, Users, Star, AlertCircle } from 'lucide-react';
import { useAuth } from '../../../providers/AuthProvider';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import type { PageProps } from '../../../types/page';
import { billingService } from '@/services/billingService';

const BillingPage: React.FC<PageProps> = ({ nav = true, footer = false }) => {
    const { user } = useAuth();
    const [error, setError] = useState<string | null>(null);
    const [successMessage] = useState<string | null>(null);

    // Billing info from user
    const billingInfo = user?.billingInfo;
    const subscription = billingInfo ? {
        tier: billingInfo.subscriptionTier,
        status: billingInfo.subscriptionStatus || 'N/A',
        currentPeriodEnd: billingInfo.currentPeriodEnd,
        teamId: billingInfo.teamId,
    } : null;

    const loading = !user;

    const getTierIcon = (tier: string) => {
        switch (tier) {
            case 'free':
                return <CreditCard className="h-5 w-5" />;
            case 'starter':
                return <Star className="h-5 w-5" />;
            case 'team':
                return <Users className="h-5 w-5" />;
            default:
                return <CreditCard className="h-5 w-5" />;
        }
    };

    const getTierColor = (tier: string) => {
        switch (tier) {
            case 'free':
                return 'bg-muted text-muted-foreground';
            case 'starter':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
            case 'team':
                return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
            default:
                return 'bg-muted text-muted-foreground';
        }
    };

    if (loading) {
        return (
            <div className="container mx-auto py-8">
                <div className="flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <span className="ml-2">Loading billing information...</span>
                </div>
            </div>
        );
    }

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="container mx-auto py-8 space-y-8">
                {/* Header */}
                <div className="space-y-2">
                    <h1 className="text-3xl font-bold">Billing & Subscriptions</h1>
                    <p className="text-muted-foreground">
                        Manage your subscription and billing information
                    </p>
                </div>

                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {successMessage && (
                    <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/50">
                        <AlertCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                        <AlertDescription className="text-green-800 dark:text-green-200">
                            {successMessage}
                        </AlertDescription>
                    </Alert>
                )}

                {/* Current Subscription */}
                {subscription && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                {getTierIcon(subscription.tier)}
                                Current Subscription
                            </CardTitle>
                            <CardDescription>
                                Your active subscription and billing details
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <Badge className={getTierColor(subscription.tier)}>
                                            {subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1)} Plan
                                        </Badge>
                                        <Badge variant="outline" className="text-green-600">
                                            Status: {subscription.status}
                                        </Badge>
                                    </div>
                                    {subscription.currentPeriodEnd && (
                                        <p className="text-sm text-muted-foreground">
                                            Next billing date: {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                                        </p>
                                    )}
                                </div>
                                {subscription.tier !== 'free' && (
                                    <Button
                                        variant="outline"
                                        onClick={async () => {
                                            try {
                                                const { url } = await billingService.getPortalUrl();
                                                window.location.href = url;
                                            } catch (e) {
                                                setError('Failed to open billing portal.');
                                            }
                                        }}
                                    >
                                        Manage Billing
                                    </Button>
                                )}
                            </div>

                            {billingInfo?.teamId && (
                                <div className="p-4 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-200 dark:border-blue-800">
                                    <h4 className="font-medium text-blue-900 dark:text-blue-100">Team Subscription</h4>
                                    <p className="text-sm text-blue-700 dark:text-blue-300">
                                        This subscription is associated with a team workspace.
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Subscription Plans */}
                <div className="space-y-6">
                    <div>
                        <h2 className="text-2xl font-bold mb-2">Available Plans</h2>
                        <p className="text-muted-foreground">
                            Choose the plan that best fits your needs. You can upgrade or downgrade at any time.
                        </p>
                    </div>
                    <PricingTiers
                        currentTier={subscription?.tier}
                        hideButtons={false}
                        className="pt-2"
                    />
                </div>
            </div>
        </PageLayout>
    );
};

export default BillingPage;
